import { z } from 'zod';

/** Nama asisten. Satu tempat supaya UI web, admin, dan prompt tidak berbeda eja. */
export const ASSISTANT_NAME = 'Imma';

/** Panjang pesan yang boleh diketik pengguna dalam satu giliran. */
export const ASSISTANT_MESSAGE_MAX = 2000;

/** Jawaban GET .../status: dipakai klien untuk menampilkan atau menyembunyikan Imma. */
export interface AssistantStatus {
  enabled: boolean;
  name: string;
  maxTurns: number;
  /** URL avatar dari CDN; kecil untuk tombol dan gelembung, besar untuk sapaan. */
  avatar: { small: string; large: string };
}

export const assistantChannelSchema = z.enum(['WEB', 'ADMIN']);
export type AssistantChannel = z.infer<typeof assistantChannelSchema>;

export const assistantMessageInputSchema = z.object({
  message: z.string().trim().min(1).max(ASSISTANT_MESSAGE_MAX),
});
export type AssistantMessageInput = z.infer<typeof assistantMessageInputSchema>;

export const assistantConversationParamsSchema = z.object({
  id: z.string().min(1).max(64),
});
export type AssistantConversationParams = z.infer<typeof assistantConversationParamsSchema>;

export const assistantMessageParamsSchema = z.object({
  messageId: z.string().min(1).max(64),
});
export type AssistantMessageParams = z.infer<typeof assistantMessageParamsSchema>;

export const assistantFeedbackSchema = z.object({
  rating: z.union([z.literal(1), z.literal(-1), z.null()]),
});
export type AssistantFeedbackInput = z.infer<typeof assistantFeedbackSchema>;

/** Tinjauan percakapan oleh admin. */
export const adminAssistantQuerySchema = z.object({
  channel: assistantChannelSchema.optional(),
  /** Hanya percakapan yang punya umpan balik negatif. */
  flagged: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type AdminAssistantQuery = z.infer<typeof adminAssistantQuerySchema>;

// ------------------------------------------------------------
// Kartu UI. Diisi server dari hasil tool yang sudah divalidasi terhadap
// database dan mesin harga, bukan dari teks model. Angka di kartu inilah
// yang boleh dipercaya pelanggan.
// ------------------------------------------------------------

export interface AssistantProductCard {
  type: 'product';
  slug: string;
  name: string;
  tagline: string | null;
  categoryName: string | null;
  basePrice: number;
  priceUnit: string;
  minQty: number;
  leadTimeDays: number;
  imageUrl: string | null;
}

export interface AssistantPriceCard {
  type: 'price';
  slug: string;
  productName: string;
  selections: Record<string, string>;
  optionLabels: { label: string; valueLabel: string }[];
  qty: number;
  unitPrice: number;
  total: number;
  leadTimeDays: number;
  /** false di kanal admin: staf tidak menambah ke keranjang dari chat. */
  canAddToCart: boolean;
}

export interface AssistantShippingCard {
  type: 'shipping';
  destinationLabel: string;
  billableWeightGram: number;
  /** Ringkasan isi paket yang dihitung, mis. "Stiker Vinyl ×100". */
  basis: string;
  options: {
    courierName: string;
    service: string;
    cost: number;
    etd: string | null;
    group: string;
  }[];
}

export interface AssistantOrderCard {
  type: 'order';
  number: string;
  status: string;
  total: number;
  createdAt: string;
  trackingNumber: string | null;
  itemsSummary: string;
  /** Tautan relatif di aplikasi yang sedang dipakai (web atau admin). */
  href: string;
}

export interface AssistantCustomerCard {
  type: 'customer';
  id: string;
  name: string;
  email: string;
  orderCount: number;
  href: string;
}

export interface AssistantActionCard {
  type: 'action';
  kind: 'login' | 'contact' | 'cart' | 'checkout';
  label: string;
  href: string;
}

export type AssistantCard =
  | AssistantProductCard
  | AssistantPriceCard
  | AssistantShippingCard
  | AssistantOrderCard
  | AssistantCustomerCard
  | AssistantActionCard;

// ------------------------------------------------------------
// Tampilan percakapan dan event streaming
// ------------------------------------------------------------

export interface AssistantDisplayItem {
  /** Untuk item asisten: id pesan asisten terakhir di giliran itu (target umpan balik). */
  id: string;
  role: 'user' | 'assistant';
  text: string;
  cards: AssistantCard[];
  feedback: 1 | -1 | null;
  createdAt: string;
}

export interface AssistantConversationSummary {
  id: string;
  channel: AssistantChannel;
  title: string | null;
  userTurns: number;
  lastMessageAt: string;
  createdAt: string;
}

export interface AssistantConversationDetail extends AssistantConversationSummary {
  items: AssistantDisplayItem[];
  /** Sisa giliran sebelum pengguna diminta memulai percakapan baru. */
  remainingTurns: number;
}

/**
 * Event SSE dari POST .../messages. Satu baris `data:` per event, JSON.
 * Urutan normal: start → (text | status | card)* → done. Bila gagal: error.
 */
export type AssistantStreamEvent =
  | { type: 'start'; conversationId: string }
  | { type: 'text'; delta: string }
  | { type: 'status'; label: string }
  | { type: 'card'; card: AssistantCard }
  | { type: 'done'; messageId: string; remainingTurns: number }
  | { type: 'error'; code: string; message: string };
